import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import translations from './agreementcontent.i18n';
import * as css from './agreementcontent.module.scss';
import * as commonCss from './avtalesectionstyles.module.scss';
import { ConfirmationDialog } from '../../components/dialog';
import { PDPFileUpload } from '../../components/fileupload';
import { TextInput } from '../../components/input';
import Button from '../../components/button';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import classNames from 'classnames';
import Checkbox from '../../components/input/checkbox';
import { AgreementStateActionType, useAgreementContext } from './index';
import {
  Avtalevedlegg,
  dispatchTicketAndExtendedVedleggInfo,
  FileFromFrontend,
  Vedlegg,
  VedleggFileFromFrontend,
} from './vedleggutils';
import { PraksisavtaleVedleggTypeDto } from '../../api/generated/studiested-generated-api';
import { RadioButton, RadioGroup } from '../../components/input/radioButton';
import { uuidv4 } from '../../util/uuidv4';
import useAddErrorNotificationCallback from '../../hooks/useAddErrorNotificationCallback';
import { clickLink } from '../../util/link';
import { Link, useLocation } from 'react-router-dom';
import { downloadFile } from '../../util/download';
import { useStudiestedOrPraksisstedApi } from '../../api/useApi';

enum DeleteType {
  ALL,
  SINGLE,
}

const AgreementContent = () => {
  const { alreadySigned, dispatchAgreementEdit, vedlegg, canEditAgreement } =
    useAgreementContext();

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [overwriteVedlegg, setOverwriteVedlegg] = useState<
    | Vedlegg<
        | typeof PraksisavtaleVedleggTypeDto.Samarbeidsavtale
        | typeof PraksisavtaleVedleggTypeDto.Databehandleravtale
      >
    | undefined
  >();
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);

  const { formatMessage } = useIntl();
  const labels = {
    addAvtaleAttachment: formatMessage({
      id: translations.addAvtaleAttachment.id,
    }),
    deleteAvtaleAttachments: formatMessage({
      id: translations.deleteAvtaleAttachments.id,
    }),
    alreadySignedCheckbox: formatMessage({
      id: translations.alreadySignedCheckbox.id,
    }),
  };

  const setAlreadySigned = (checked: boolean) =>
    dispatchAgreementEdit({
      type: AgreementStateActionType.SET_ALREADY_SIGNED,
      payload: checked,
    });

  const onClose = (newVedlegg?: Vedlegg<PraksisavtaleVedleggTypeDto>) => {
    if (newVedlegg) {
      const { type } = newVedlegg;

      const willReplaceSamarbeidsavtale =
        !!vedlegg.samarbeidsavtale &&
        type === PraksisavtaleVedleggTypeDto.Samarbeidsavtale;
      const willReplaceDatabehandlerAvtale =
        !!vedlegg.databehandleravtale &&
        type === PraksisavtaleVedleggTypeDto.Databehandleravtale;
      const willReplace =
        willReplaceSamarbeidsavtale || willReplaceDatabehandlerAvtale;

      if (
        newVedlegg?.type === PraksisavtaleVedleggTypeDto.Annet ||
        !willReplace
      ) {
        dispatchAgreementEdit({
          type: AgreementStateActionType.NEW_VEDLEGG,
          payload: newVedlegg,
        });
      } else {
        setOverwriteVedlegg(
          newVedlegg as Vedlegg<
            | typeof PraksisavtaleVedleggTypeDto.Samarbeidsavtale
            | typeof PraksisavtaleVedleggTypeDto.Databehandleravtale
          >,
        );
      }
    }

    setModalIsOpen(false);
  };

  const onOverwriteVedleggClose = (confirm: boolean) => {
    if (confirm && overwriteVedlegg) {
      dispatchAgreementEdit({
        type: AgreementStateActionType.NEW_VEDLEGG,
        payload: overwriteVedlegg,
      });
    }

    setOverwriteVedlegg(undefined);
  };

  const hasAnyVedlegg = useMemo<boolean>(
    () =>
      !!(
        vedlegg.samarbeidsavtale ||
        vedlegg.databehandleravtale ||
        vedlegg.annet.length
      ),
    [vedlegg],
  );

  const onDeleteAll = (confirm: boolean) => {
    if (confirm) {
      dispatchAgreementEdit({
        type: AgreementStateActionType.DELETE_ALL_VEDLEGG,
      });
    }

    setDeleteAllModalOpen(false);
  };

  return (
    <div id={'avtaleContent'}>
      {hasAnyVedlegg && <VedleggTable />}
      {canEditAgreement && (
        <div
          className={classNames(
            commonCss.actionBtns,
            hasAnyVedlegg && commonCss.btnMargin,
          )}
        >
          {hasAnyVedlegg && (
            <Button.Tertiary
              className={commonCss.button}
              onClick={() => setDeleteAllModalOpen(true)}
              data-cy={'delete-all-vedlegg'}
            >
              {labels.deleteAvtaleAttachments}
            </Button.Tertiary>
          )}
          <Button.Secondary
            className={commonCss.button}
            onClick={() => setModalIsOpen(true)}
            data-cy={'add-vedlegg'}
          >
            {labels.addAvtaleAttachment}
          </Button.Secondary>
        </div>
      )}
      <div className={commonCss.checkboxes}>
        <Checkbox
          id={'alreadySigned'}
          label={labels.alreadySignedCheckbox}
          checked={alreadySigned}
          setChecked={setAlreadySigned}
          disabled={!canEditAgreement}
        />
      </div>
      {modalIsOpen && <AgreementContentModal onClose={onClose} />}
      {overwriteVedlegg && (
        <WarningReplaceWithConfirmation
          type={overwriteVedlegg.type}
          onClose={onOverwriteVedleggClose}
        />
      )}
      {deleteAllModalOpen && (
        <WarningDeleteWithConfirmation
          onClose={onDeleteAll}
          type={DeleteType.ALL}
        />
      )}
    </div>
  );
};

const VedleggHeader = {
  Lenke: 'Lenke',
  Type: 'Type',
  Handling: 'Handling',
} as const;

const VedleggTable = () => {
  const { canEditAgreement } = useAgreementContext();
  const { formatMessage } = useIntl();

  const headerLabels = useMemo(
    () => ({
      [VedleggHeader.Lenke]: formatMessage({
        id: translations.attachment.id,
      }),
      [VedleggHeader.Type]: formatMessage({ id: translations.vedleggstype.id }),
      [VedleggHeader.Handling]: formatMessage({ id: translations.handling.id }),
    }),
    [formatMessage],
  );

  const headerLabelsView = useMemo(
    () => ({
      [VedleggHeader.Lenke]: formatMessage({
        id: translations.attachment.id,
      }),
      [VedleggHeader.Type]: formatMessage({ id: translations.vedleggstype.id }),
    }),
    [formatMessage],
  );

  const tableHeaders = useMemo(
    () =>
      Object.entries(canEditAgreement ? headerLabels : headerLabelsView).map(
        ([type, label]) => (
          <TableCellTh
            key={`${type}-${label}`}
            align={type === headerLabels.Handling ? 'right' : 'inherit'}
          >
            {label}
          </TableCellTh>
        ),
      ),
    [headerLabels, headerLabelsView, canEditAgreement],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{tableHeaders}</TableRow>
      </TableHeader>
      <TableMain>
        <VedleggTableRows headers={headerLabels} />
      </TableMain>
    </Table>
  );
};

interface VedleggTableRowsProps {
  headers: { [key in keyof typeof VedleggHeader]: string };
}

const VedleggTableRows: React.FC<
  React.PropsWithChildren<VedleggTableRowsProps>
> = ({ headers }) => {
  const { vedlegg } = useAgreementContext();

  return (
    <>
      {vedlegg.samarbeidsavtale && (
        <VedleggTableRow headers={headers} vedlegg={vedlegg.samarbeidsavtale} />
      )}
      {vedlegg.databehandleravtale && (
        <VedleggTableRow
          headers={headers}
          vedlegg={vedlegg.databehandleravtale}
        />
      )}
      {vedlegg.annet.map((annetVedlegg, index) => (
        <VedleggTableRow
          key={`annet-vedlegg-${index}`}
          vedlegg={annetVedlegg}
          headers={headers}
        />
      ))}
    </>
  );
};

interface VedleggTableRowProps extends VedleggTableRowsProps {
  vedlegg: Vedlegg<PraksisavtaleVedleggTypeDto>;
}

const VedleggTableRow: React.FC<
  React.PropsWithChildren<VedleggTableRowProps>
> = ({ headers, vedlegg }) => {
  const { canEditAgreement } = useAgreementContext();
  return (
    <TableRow>
      <LenkeCell header={headers.Lenke} vedlegg={vedlegg} />
      <TypeCell header={headers.Type} vedlegg={vedlegg} />
      {canEditAgreement && (
        <HandlingCell header={headers.Handling} vedlegg={vedlegg} />
      )}
    </TableRow>
  );
};

interface VedleggTableCellProps {
  header: string;
  vedlegg: Vedlegg<PraksisavtaleVedleggTypeDto>;
}

const LenkeCell: React.FC<React.PropsWithChildren<VedleggTableCellProps>> = ({
  header,
  vedlegg,
}) => {
  const {
    vedleggFile: { fileFromFrontend, fileFromBackend },
  } = vedlegg;

  return (
    <TableCellTd header={header}>
      {fileFromFrontend && <LenkeFromFrontend vedlegg={fileFromFrontend} />}
      {fileFromBackend && <LenkeFromBackend vedlegg={vedlegg} />}
    </TableCellTd>
  );
};

interface LenkeFromFrontendProps {
  vedlegg: FileFromFrontend;
}

const LenkeFromFrontend: React.FC<
  React.PropsWithChildren<LenkeFromFrontendProps>
> = ({ vedlegg }) => {
  const location = useLocation();
  const { file } = vedlegg;

  const onClick = () => {
    downloadFile(file);
  };

  return (
    <Link to={location} onClick={onClick}>
      {file.name}
    </Link>
  );
};

interface LenkeFromBackendProps {
  vedlegg: Vedlegg<PraksisavtaleVedleggTypeDto>;
}

const LenkeFromBackend: React.FC<
  React.PropsWithChildren<LenkeFromBackendProps>
> = ({ vedlegg }) => {
  const { avtaleApi } = useStudiestedOrPraksisstedApi();
  const { avtaleId: avtaleIdUndefined, dispatchAgreementEdit } =
    useAgreementContext();

  const avtaleId = avtaleIdUndefined!;

  const {
    id: vedleggId,
    vedleggFile: { fileFromBackend },
  } = vedlegg;

  const {
    minimal: { navn },
  } = fileFromBackend!;

  const catchError = useAddErrorNotificationCallback();

  const onClick = () => {
    avtaleApi
      .avtalerPraksisAvtaleIdVedleggVedleggIdGet({
        pathParams: {
          avtaleId,
          vedleggId,
        },
      })
      .then((vedleggWithTicketDto) => {
        dispatchTicketAndExtendedVedleggInfo(
          vedlegg,
          vedleggWithTicketDto,
          dispatchAgreementEdit,
        );

        clickLink(vedleggWithTicketDto.vedlegg);
      })
      .catch(catchError);
  };

  const location = useLocation();

  return (
    <Link to={location} onClick={onClick}>
      {navn}
    </Link>
  );
};

const TypeCell: React.FC<React.PropsWithChildren<VedleggTableCellProps>> = ({
  header,
  vedlegg,
}) => {
  const { formatMessage } = useIntl();
  const labels = useMemo<{
    [key in keyof typeof PraksisavtaleVedleggTypeDto]: string;
  }>(
    () => ({
      [PraksisavtaleVedleggTypeDto.Samarbeidsavtale]: formatMessage({
        id: translations.samarbeidsavtale.id,
      }),
      [PraksisavtaleVedleggTypeDto.Databehandleravtale]: formatMessage({
        id: translations.databehandleravtale.id,
      }),
      [PraksisavtaleVedleggTypeDto.Annet]: formatMessage({
        id: translations.annet.id,
      }),
    }),
    [formatMessage],
  );

  return <TableCellTd header={header}>{labels[vedlegg.type]}</TableCellTd>;
};

const useRemainingVedleggAreInitialFunc = (): ((
  filteredVedlegg: Avtalevedlegg,
) => boolean) => {
  const { initVedlegg } = useAgreementContext();

  return (filteredVedlegg: Avtalevedlegg) => {
    const filteredAnnetIds = filteredVedlegg.annet.map((vedlegg) => vedlegg.id);

    if (
      (initVedlegg.samarbeidsavtale && !filteredVedlegg.samarbeidsavtale) ||
      (initVedlegg.databehandleravtale &&
        !filteredVedlegg.databehandleravtale) ||
      !initVedlegg.annet.every((vedlegg) =>
        filteredAnnetIds.includes(vedlegg.id),
      )
    ) {
      return false;
    }

    return (
      !filteredVedlegg.samarbeidsavtale?.vedleggFile?.fileFromFrontend &&
      !filteredVedlegg.databehandleravtale?.vedleggFile?.fileFromFrontend &&
      filteredVedlegg.annet.every(
        (vedlegg) => !vedlegg.vedleggFile?.fileFromFrontend,
      )
    );
  };
};

const useVedleggHasChangedAfterDelete = (): ((
  vedleggToDelete: Vedlegg<PraksisavtaleVedleggTypeDto>,
) => boolean) => {
  const { vedlegg: currentVedlegg } = useAgreementContext();
  const remainingVedleggAreInitialFunc = useRemainingVedleggAreInitialFunc();

  return (vedleggToDelete: Vedlegg<PraksisavtaleVedleggTypeDto>) => {
    switch (vedleggToDelete.type) {
      case PraksisavtaleVedleggTypeDto.Samarbeidsavtale:
        return (
          !!vedleggToDelete.vedleggFile.fileFromBackend ||
          !remainingVedleggAreInitialFunc({
            ...currentVedlegg,
            samarbeidsavtale: undefined,
          })
        );
      case PraksisavtaleVedleggTypeDto.Databehandleravtale:
        return (
          !!vedleggToDelete.vedleggFile.fileFromBackend ||
          !remainingVedleggAreInitialFunc({
            ...currentVedlegg,
            databehandleravtale: undefined,
          })
        );
      case PraksisavtaleVedleggTypeDto.Annet:
        return (
          !!vedleggToDelete.vedleggFile.fileFromBackend ||
          !remainingVedleggAreInitialFunc({
            ...currentVedlegg,
            annet: currentVedlegg.annet.filter(
              (vedlegg) => vedlegg.id !== vedleggToDelete.id,
            ),
          })
        );
    }
  };
};

const HandlingCell: React.FC<
  React.PropsWithChildren<VedleggTableCellProps>
> = ({ header, vedlegg }) => {
  const { dispatchAgreementEdit } = useAgreementContext();
  const { formatMessage } = useIntl();
  const deleteVedleggLabel = useMemo(
    () => formatMessage({ id: translations.deleteVedlegg.id }),
    [formatMessage],
  );

  const [warningModalOpen, setWarningModalOpen] = useState(false);

  const vedleggHasChanged = useVedleggHasChangedAfterDelete();

  const onClick = (confirm: boolean) => {
    if (confirm) {
      dispatchAgreementEdit({
        type: AgreementStateActionType.DELETE_VEDLEGG,
        payload: {
          content: vedlegg.id,
          vedleggHasChanged: vedleggHasChanged(vedlegg),
        },
      });
    }

    setWarningModalOpen(false);
  };

  return (
    <>
      <TableCellTd header={header} align={'right'}>
        <Button.Tertiary
          onClick={() => setWarningModalOpen(true)}
          data-cy={'delete-vedlegg-btn'}
        >
          {deleteVedleggLabel}
        </Button.Tertiary>
      </TableCellTd>
      {warningModalOpen && (
        <WarningDeleteWithConfirmation
          type={DeleteType.SINGLE}
          onClose={onClick}
        />
      )}
    </>
  );
};

interface PDFLabelProps {
  maxSize: number;
}

const PDFLabel: React.FC<React.PropsWithChildren<PDFLabelProps>> = ({
  maxSize,
}) => {
  const { formatMessage } = useIntl();
  const labels = {
    pdfFileInputLabelPrefix: formatMessage({
      id: translations.pdfFileInputLabelPrefix.id,
    }),
    pdfFileInputLabelLink: formatMessage({
      id: translations.pdfFileInputLabelLink.id,
    }),
    pdfFileInputLabelSuffix: formatMessage(
      { id: translations.pdfFileInputLabelSuffix.id },
      { maxSize },
    ),
  };

  return (
    <div className={css.pdfLabel}>
      {labels.pdfFileInputLabelPrefix}
      <span className={css.link}>{labels.pdfFileInputLabelLink}</span>
      {labels.pdfFileInputLabelSuffix}
    </div>
  );
};

// If filename does not end in .pdf, make it so. (Don't add if it already exists). Also replace all spaces with underscore
const formatNewName = (newName: string) =>
  newName.replace(' ', '_').replace(/\.pdf/i, '').concat('.pdf');

interface AgreementContentModalProps {
  onClose: (file: Vedlegg<PraksisavtaleVedleggTypeDto> | undefined) => void;
}

const MAX_FILE_SIZE_IN_MB = 3.5;
const AgreementContentModal: React.FC<
  React.PropsWithChildren<AgreementContentModalProps>
> = ({ onClose }) => {
  const [file, setFile] = useState<File>();
  const [name, setName] = useState<string>();
  const [type, setType] = useState<PraksisavtaleVedleggTypeDto>(
    PraksisavtaleVedleggTypeDto.Samarbeidsavtale,
  );

  const { formatMessage } = useIntl();
  const labels = {
    upload: formatMessage({ id: translations.upload.id }),
    cancel: formatMessage({ id: translations.cancel.id }),
    pdfFileInputModalTitle: formatMessage({
      id: translations.pdfFileInputModalTitle.id,
    }),
    pdfFileInputModalSubtitle: formatMessage({
      id: translations.pdfFileInputModalSubtitle.id,
    }),
    pdfFileInputNameFieldLabel: formatMessage({
      id: translations.pdfFileInputNameFieldLabel.id,
    }),
    pdfFileInputNameFieldPlaceholder: formatMessage({
      id: translations.pdfFileInputNameFieldPlaceholder.id,
    }),
  };

  const close = (sendFile: boolean) => {
    if (sendFile) {
      const newFile =
        file && name
          ? new File([file], formatNewName(name), { type: file.type })
          : file;

      if (newFile) {
        const newVedlegg: VedleggFileFromFrontend = {
          fileFromFrontend: {
            file: newFile,
          },
        };

        onClose({ id: uuidv4(), type, vedleggFile: newVedlegg });
      } else {
        onClose(undefined);
      }
    } else {
      onClose(undefined);
    }
  };

  const onChange = (newFile?: File) => {
    setFile(newFile);
  };

  const closeOnInputEnterKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') close(true);
  };

  return (
    <ConfirmationDialog
      extraClasses={[commonCss.modal]}
      title={labels.pdfFileInputModalTitle}
      subtitle={labels.pdfFileInputModalSubtitle}
      submitLabel={labels.upload}
      cancelLabel={labels.cancel}
      onClose={close}
      submitDisabled={!file}
      isOpen
    >
      <PDPFileUpload
        id={'agreementcontent-pdf'}
        label={<PDFLabel maxSize={MAX_FILE_SIZE_IN_MB} />}
        onChange={onChange}
        maxSizeInMB={MAX_FILE_SIZE_IN_MB}
      />
      <TextInput
        labelClassName={css.inputLabel}
        value={name || ''}
        htmlId={'attachment-name'}
        onChange={setName}
        onKeyDown={closeOnInputEnterKey}
        disabled={!file}
        placeholder={labels.pdfFileInputNameFieldPlaceholder}
        label={labels.pdfFileInputNameFieldLabel}
      />
      <ChooseVedleggTypeRadioGroup chosenType={type} setChosenType={setType} />
    </ConfirmationDialog>
  );
};

interface ChooseVedleggTypeRadioGroupProps {
  chosenType: keyof typeof PraksisavtaleVedleggTypeDto;
  setChosenType: (type: keyof typeof PraksisavtaleVedleggTypeDto) => void;
}

const VEDLEGG_TYPE_NAME = 'VEDLEGG_TYPE_NAME';

const ChooseVedleggTypeRadioGroup: React.FC<
  React.PropsWithChildren<ChooseVedleggTypeRadioGroupProps>
> = ({ chosenType, setChosenType }) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      vedleggstype: formatMessage({ id: translations.vedleggstype.id }),
      samarbeidsavtale: formatMessage({ id: translations.samarbeidsavtale.id }),
      databehandleravtale: formatMessage({
        id: translations.databehandleravtale.id,
      }),
      annet: formatMessage({ id: translations.annet.id }),
    }),
    [formatMessage],
  );

  const handleChange = (value: string) => {
    setChosenType(value as PraksisavtaleVedleggTypeDto);
  };

  return (
    <RadioGroup label={labels.vedleggstype} className={css.radioGroup}>
      <RadioButton
        id={'vedleggstype-samarbeidsavtale'}
        label={labels.samarbeidsavtale}
        value={PraksisavtaleVedleggTypeDto.Samarbeidsavtale}
        checked={chosenType === PraksisavtaleVedleggTypeDto.Samarbeidsavtale}
        name={VEDLEGG_TYPE_NAME}
        handleChange={handleChange}
      />
      <RadioButton
        id={'vedleggstype-databehandleravtale'}
        label={labels.databehandleravtale}
        value={PraksisavtaleVedleggTypeDto.Databehandleravtale}
        checked={chosenType === PraksisavtaleVedleggTypeDto.Databehandleravtale}
        name={VEDLEGG_TYPE_NAME}
        handleChange={handleChange}
      />
      <RadioButton
        id={'vedleggstype-annet'}
        label={labels.annet}
        value={PraksisavtaleVedleggTypeDto.Annet}
        checked={chosenType === PraksisavtaleVedleggTypeDto.Annet}
        name={VEDLEGG_TYPE_NAME}
        handleChange={handleChange}
      />
    </RadioGroup>
  );
};

interface WarningWithConfirmationProps {
  warningMsg: string;
  submitLabel: string;
  onClose: (confirm: boolean) => void;
}

const WarningWithConfirmation: React.FC<
  React.PropsWithChildren<WarningWithConfirmationProps>
> = ({ warningMsg, submitLabel, onClose }) => {
  const { formatMessage } = useIntl();
  const title = useMemo(
    () => formatMessage({ id: translations.warning.id }),
    [formatMessage],
  );

  return (
    <ConfirmationDialog
      title={title}
      submitLabel={submitLabel}
      onClose={onClose}
      isOpen
    >
      <p>{warningMsg}</p>
    </ConfirmationDialog>
  );
};

interface WarningReplaceWithConfirmationProps {
  onClose: (confirm: boolean) => void;
  type:
    | typeof PraksisavtaleVedleggTypeDto.Samarbeidsavtale
    | typeof PraksisavtaleVedleggTypeDto.Databehandleravtale;
}

const WarningReplaceWithConfirmation: React.FC<
  React.PropsWithChildren<WarningReplaceWithConfirmationProps>
> = ({ onClose, type }) => {
  const { formatMessage } = useIntl();
  const typeLabels = useMemo<{
    [key in
      | typeof PraksisavtaleVedleggTypeDto.Samarbeidsavtale
      | typeof PraksisavtaleVedleggTypeDto.Databehandleravtale]: string;
  }>(
    () => ({
      [PraksisavtaleVedleggTypeDto.Samarbeidsavtale]: formatMessage({
        id: translations.samarbeidsavtale.id,
      }),
      [PraksisavtaleVedleggTypeDto.Databehandleravtale]: formatMessage({
        id: translations.databehandleravtale.id,
      }),
    }),
    [formatMessage],
  );

  const labels = useMemo(
    () => ({
      replaceSubmit: formatMessage(
        { id: translations.replaceSubmit.id },
        { type: typeLabels[type] },
      ),
      replaceWarning: formatMessage(
        { id: translations.replaceWarning.id },
        { type: typeLabels[type] },
      ),
    }),
    [formatMessage, typeLabels, type],
  );

  return (
    <WarningWithConfirmation
      warningMsg={labels.replaceWarning}
      submitLabel={labels.replaceSubmit}
      onClose={onClose}
    />
  );
};

interface WarningDeleteWithConfirmationProps {
  type: DeleteType;
  onClose: (confirm: boolean) => void;
}

const WarningDeleteWithConfirmation: React.FC<
  React.PropsWithChildren<WarningDeleteWithConfirmationProps>
> = ({ onClose, type }) => {
  const { formatMessage } = useIntl();

  const labels = useMemo(
    () => ({
      deleteSubmit: formatMessage({ id: translations.deleteSubmit.id }),
      deleteWarning: formatMessage({ id: translations.deleteWarning.id }),
      deleteAllWarning: formatMessage({ id: translations.deleteAllWarning.id }),
    }),
    [formatMessage],
  );

  return (
    <WarningWithConfirmation
      warningMsg={
        type === DeleteType.ALL ? labels.deleteAllWarning : labels.deleteWarning
      }
      submitLabel={labels.deleteSubmit}
      onClose={onClose}
    />
  );
};

export default AgreementContent;
